import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Search, Edit, Save, Calendar, User, Loader2, AlertCircle, UserPlus, X, Plus, AlertTriangle, Play, Pause, Volume2, VolumeX, Share2, MoreVertical, Archive, Trash2, Filter, SortAsc, SortDesc, Check, RefreshCw, Mic, Globe, ChevronLeft, ExternalLink, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';
import { Textarea } from '@/components/ui/textarea';
import InviteCandidatesDialog from '@/components/assessment/InviteCandidatesDialog';
import CandidateCard, { Candidate as CandidateType } from '@/components/CandidateCard';
import { useCreditsForAssessment, getUserCredits } from '@/services/creditService';
import { getUserSubscription, isBasicPlan, Subscription } from '@/services/subscriptionService';
import { Link } from 'react-router-dom';
import AssessmentTranscript from '@/components/assessment/AssessmentTranscript';
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import AssessmentRecording from '../components/assessment/AssessmentRecording';
import AssessmentSummary from '../components/assessment/AssessmentSummary';
import PerformanceComparison from '../components/assessment/PerformanceComparison';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { evaluateAssessment, type AssessmentEvaluation } from '@/services/openaiService';
import { useSelectCandidate } from '@/hooks/useSelectCandidate';
import CandidateSheet from '@/components/candidate/CandidateSheet';
import { startAtsApplicantsSync } from '@/services/knitService';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Import the language and accent types
type AccentOption = {
  id: string;
  name: string;
  voiceId: string;
};

type AccentsType = {
  [key: string]: AccentOption[];
};

// Hardcoded data from language_accents.csv - same as in creation components
const LANGUAGES = [
  { id: 'Arabic', name: 'Arabic' },
  { id: 'Chinese', name: 'Chinese' },
  { id: 'English', name: 'English' },
  { id: 'French', name: 'French' },
  { id: 'German', name: 'German' }
];

const ACCENTS: AccentsType = {
  'Arabic': [
    { id: 'modern standard', name: 'modern standard', voiceId: 'a1KZUXKFVFDOb33I1uqr' }
  ],
  'Chinese': [
    { id: 'Mandarin Beijing', name: 'Mandarin Beijing', voiceId: 'bhJUNIXWQQ94l8eI2VUf' }
  ],
  'English': [
    { id: 'American', name: 'American', voiceId: 'g6xIsTj2HwM6VR4iXFCw' },
    { id: 'Australian', name: 'Australian', voiceId: 'ys3XeJJA4ArWMhRpcX1D' },
    { id: 'British', name: 'British', voiceId: 'Fahco4VZzobUeiPqni1S' },
    { id: 'Indian', name: 'Indian', voiceId: '90ipbRoKi4CpHXvKVtl0' }
  ],
  'French': [
    { id: 'Standard', name: 'Standard', voiceId: 'MNKK2Wl2wbbsEPQTHZGt' }
  ],
  'German': [
    { id: 'Standard', name: 'Standard', voiceId: 'aTTiK3YzK3dXETpuDE2h' }
  ]
};

type AssessmentRow = {
  id: string;
  title: string;
  user_id: string;
  status: 'completed' | 'draft' | 'active' | 'inactive' | 'archived';
  created_at: string;
  updated_at: string;
  skills: string[];
  custom_questions: string[];
  skill_questions?: Record<string, string[]>;
  language?: string;
  accent?: string;
  voice_id?: string;
  source?: string;
  ats_job_id?: string;
  organization_id?: string;
  candidates_invited?: number;
  candidates_completed?: number;
  assessmentType?: string;
  assessment_type?: string;
};

type ExtendedCandidate = CandidateType & {
  ai_rating?: number;
  created_at: string;
  fit_status?: 'good_fit' | 'maybe' | 'not_fit' | null;
};

type TranscriptEntry = {
  source: string;
  message: string;
  timestamp: string;
};

type CandidateAssessment = {
  audio_url?: string;
  transcript?: TranscriptEntry[];
};

// Add the HTML pattern check constant at the top of the file
const HTML_PATTERN = /<[^>]*>/g;
const URL_PATTERN = /https?:\/\/\S+/i;

const AssessmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isRecruiterVerified } = useAuth();
  const [assessment, setAssessment] = useState<AssessmentRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Update isEditing type
  const [isEditing, setIsEditing] = useState<'title' | 'skills' | 'questions' | 'skillQuestions' | 'interviewSettings' | null>(null);
  
  // Editing state
  const [editedTitle, setEditedTitle] = useState('');
  const [editedSkills, setEditedSkills] = useState<string[]>([]);
  const [editedQuestions, setEditedQuestions] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [isAddingSkill, setIsAddingSkill] = useState(false);
  const newSkillInputRef = useRef<HTMLInputElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Subscription state
  const [userSubscription, setUserSubscription] = useState<Subscription | null>(null);
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  const [premiumDialogContext, setPremiumDialogContext] = useState<'skills' | 'questions'>('skills');
  
  // Invite dialog state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [candidates, setCandidates] = useState<ExtendedCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const [userCredits, setUserCredits] = useState<number | null>(null);
  const [isActivating, setIsActivating] = useState(false);

  const {
    selectedCandidate,
    candidateAssessment,
    handleCandidateClick,
    handleCloseCandidate
  } = useSelectCandidate();

  const [showTranscript, setShowTranscript] = useState(false);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [evaluation, setEvaluation] = useState<AssessmentEvaluation | null>(null);

  const [completionRate, setCompletionRate] = useState<string>('0 of 0 completed');

  // Add new state for filters and sorting
  const [statusFilter, setStatusFilter] = useState<'all' | 'invited' | 'in_progress' | 'completed'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'score'>('score');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Add state for ATS integration
  const [hasActiveIntegration, setHasActiveIntegration] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  // Check if the recruiter is verified
  const isVerified = isRecruiterVerified();

  // New state for skill questions and interview settings
  const [editedSkillQuestions, setEditedSkillQuestions] = useState<Record<string, string[]>>({});
  const [newSkillQuestion, setNewSkillQuestion] = useState('');
  const [activeSkill, setActiveSkill] = useState<string | null>(null);
  
  // Interview settings state
  const [editedLanguage, setEditedLanguage] = useState('en');
  const [editedAccent, setEditedAccent] = useState('us');
  const [editedVoiceId, setEditedVoiceId] = useState('');

  useEffect(() => {
    if (user) {
      fetchAssessment();
      fetchCandidates();
      fetchUserCredits();
      fetchUserSubscription();
      checkActiveIntegration();
    }
  }, [user, id]);

  useEffect(() => {
    if (isAddingSkill && newSkillInputRef.current) {
      newSkillInputRef.current.focus();
    }
  }, [isAddingSkill]);

  useEffect(() => {
    if (user) {
      fetchUserCredits();
      checkActiveIntegration();
    }
  }, [user]);

  useEffect(() => {
    if (candidateAssessment?.audio_url) {
      const audio = new Audio(candidateAssessment.audio_url);
      setAudioRef(audio);

      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });

      audio.addEventListener('timeupdate', () => {
        setCurrentTime(audio.currentTime);
      });

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });

      return () => {
        audio.pause();
        audio.removeEventListener('loadedmetadata', () => {});
        audio.removeEventListener('timeupdate', () => {});
        audio.removeEventListener('ended', () => {});
      };
    }
  }, [candidateAssessment?.audio_url]);

  // Update useEffect to initialize edited values
  useEffect(() => {
    if (assessment) {
      setEditedTitle(assessment.title);
      setEditedSkills(assessment.skills || []);
      setEditedQuestions(assessment.custom_questions || []);
      
      // Initialize skill questions
      if (assessment.skill_questions) {
        let parsedSkillQuestions: Record<string, string[]> = {};
        if (typeof assessment.skill_questions === 'string') {
          try {
            parsedSkillQuestions = JSON.parse(assessment.skill_questions);
          } catch (e) {
            console.error("Error parsing skill_questions:", e);
          }
        } else if (typeof assessment.skill_questions === 'object') {
          parsedSkillQuestions = assessment.skill_questions;
        }
        
        setEditedSkillQuestions(parsedSkillQuestions);
        
        // Set active skill to the first one with questions if available
        const skillsWithQuestions = Object.keys(parsedSkillQuestions);
        if (skillsWithQuestions.length > 0) {
          setActiveSkill(skillsWithQuestions[0]);
        }
      }
      
      // Initialize interview settings - handle both formats (language codes and full names)
      let language = assessment.language || 'English';
      let accent = assessment.accent || 'American';
      
      // If values are language codes (en, fr, etc), convert to full names
      if (language === 'en') language = 'English';
      else if (language === 'fr') language = 'French';
      else if (language === 'de') language = 'German';
      else if (language === 'es') language = 'Spanish';
      
      // If values are accent codes (us, uk, etc), convert to full names
      if (language === 'English') {
        if (accent === 'us') accent = 'American';
        else if (accent === 'uk') accent = 'British';
        else if (accent === 'au') accent = 'Australian';
        else if (accent === 'in') accent = 'Indian';
      }
      
      setEditedLanguage(language);
      setEditedAccent(accent);
      setEditedVoiceId(assessment.voice_id || '');
    }
  }, [assessment]);

  const fetchAssessment = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log("Fetching assessment with ID:", id);
      
      const { data, error } = await supabase
        .from('assessments')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error("Error fetching assessment:", error);
        throw error;
      }
      
      if (!data) {
        console.error("No assessment found with ID:", id);
        throw new Error("Assessment not found");
      }
      
      console.log("Assessment data:", data);
      setAssessment(data);
      
      // Initialize editing state
      setEditedTitle(data.title);
      setEditedSkills(data.skills || []);
      setEditedQuestions(data.custom_questions || []);
      
      // Fetch candidates for this assessment
      fetchCandidates();
    } catch (error: any) {
      console.error("Error in fetchAssessment:", error);
      setError(error.message || "Failed to load assessment");
      toast.error('Failed to load assessment: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    if (!id) return;
    
    try {
      setLoadingCandidates(true);
      
      const { data, error } = await supabase
        .from('candidates')
        .select('*')
        .eq('assessment_id', id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform the data to match the Candidate interface
      const formattedCandidates: ExtendedCandidate[] = data.map(candidate => ({
        id: candidate.id,
        name: candidate.name,
        email: candidate.email,
        assessmentTitle: assessment?.title || '',
        status: candidate.status as 'invited' | 'in_progress' | 'completed',
        completedDate: candidate.completed_date,
        score: candidate.score,
        aiRating: candidate.ai_rating,
        potentialPlagiarism: candidate.potential_plagiarism,
        created_at: candidate.created_at,
        fit_status: candidate.fit_status
      }));
      
      setCandidates(formattedCandidates);
    } catch (error: any) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingCandidates(false);
    }
  };

  const fetchUserCredits = async () => {
    if (!user) return;
    
    try {
      const credits = await getUserCredits(user.id);
      setUserCredits(credits?.balance || 0);
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  };

  const fetchUserSubscription = async () => {
    if (!user) return;
    
    try {
      const subscription = await getUserSubscription(user.id);
      setUserSubscription(subscription);
    } catch (error) {
      console.error('Error fetching user subscription:', error);
    }
  };

  const handleRetry = () => {
    fetchAssessment();
  };

  const progress = assessment?.candidates_invited && assessment?.candidates_invited > 0 
    ? Math.round(((assessment?.candidates_completed || 0) / assessment.candidates_invited) * 100) 
    : 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'active': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const handleAddSkill = async () => {
    if (!newSkill.trim()) {
      setIsAddingSkill(false);
      return;
    }
    
    if (editedSkills.includes(newSkill)) {
      toast.error('This skill is already added');
      return;
    }
    
    // Check if the user is on Basic plan and already has 3 skills
    if (isBasicPlan(userSubscription) && editedSkills.length >= 3) {
      setPremiumDialogContext('skills');
      setShowPremiumDialog(true);
      return;
    }
    
    // Add to local state first
    const updatedSkills = [...editedSkills, newSkill];
    setEditedSkills(updatedSkills);
    setNewSkill('');
    setIsAddingSkill(false);
    
    // Automatically save to database
    if (!assessment || !id) {
      toast.error('Cannot save: Assessment data is missing');
      return;
    }
    
    try {
      setIsSaving(true);
      
      console.log('Saving updated skills to database:', updatedSkills);
      
      const { error, data } = await supabase
        .from('assessments')
        .update({
          skills: updatedSkills,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user?.id)
        .select();
      
      if (error) throw error;
      
      console.log('Skills saved successfully:', data);
      
      // Update local state
      setAssessment({
        ...assessment,
        skills: updatedSkills,
        updated_at: new Date().toISOString()
      });
      
      toast.success('Skill added successfully');
    } catch (error: any) {
      console.error('Error saving skill:', error);
      toast.error('Failed to save skill: ' + (error.message || 'Unknown error'));
      
      // Revert the local state change on error
      setEditedSkills(editedSkills); // Revert to previous state
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveSkill = async (skill: string) => {
    // Update local state
    const updatedSkills = editedSkills.filter(s => s !== skill);
    setEditedSkills(updatedSkills);
    
    // Save to database
    if (!assessment || !id) {
      toast.error('Cannot save: Assessment data is missing');
      return;
    }
    
    try {
      setIsSaving(true);
      
      console.log('Saving updated skills to database after removal:', updatedSkills);
      
      const { error, data } = await supabase
        .from('assessments')
        .update({
          skills: updatedSkills,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user?.id)
        .select();
      
      if (error) throw error;
      
      console.log('Skills saved successfully after removal:', data);
      
      // Update local state
      setAssessment({
        ...assessment,
        skills: updatedSkills,
        updated_at: new Date().toISOString()
      });
      
      toast.success('Skill removed successfully');
    } catch (error: any) {
      console.error('Error removing skill:', error);
      toast.error('Failed to remove skill: ' + (error.message || 'Unknown error'));
      
      // Revert the local state change on error
      setEditedSkills(editedSkills.filter(s => s !== skill) && editedSkills);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddQuestion = () => {
    if (!newQuestion.trim()) return;
    
    if (editedQuestions.includes(newQuestion)) {
      toast.error('This question is already added');
      return;
    }
    
    setEditedQuestions([...editedQuestions, newQuestion]);
    setNewQuestion('');
  };

  const handleRemoveQuestion = (question: string) => {
    setEditedQuestions(editedQuestions.filter(q => q !== question));
  };

  // Add new save functions for each section
  const handleSaveTitle = async () => {
    if (!assessment || !id) return;
    
    // Validate title before saving
    if (HTML_PATTERN.test(editedTitle)) {
      toast.error("HTML tags are not allowed in assessment titles");
      return;
    }
    
    if (URL_PATTERN.test(editedTitle)) {
      toast.error("URLs are not allowed in assessment titles");
      return;
    }
    
    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('assessments')
        .update({
          title: editedTitle,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      // Update local state
      setAssessment({
        ...assessment,
        title: editedTitle,
        updated_at: new Date().toISOString()
      });
      
      setIsEditing(null);
      toast.success('Assessment title updated successfully');
    } catch (error: any) {
      console.error('Error updating assessment title:', error);
      toast.error('Failed to update title: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSkills = async () => {
    if (!assessment || !id) return;
    
    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('assessments')
        .update({
          skills: editedSkills,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      // Update local state
      setAssessment({
        ...assessment,
        skills: editedSkills,
        updated_at: new Date().toISOString()
      });
      
      setIsEditing(null);
      toast.success('Skills updated successfully');
    } catch (error: any) {
      console.error('Error updating skills:', error);
      toast.error('Failed to update skills: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveQuestions = async () => {
    if (!assessment || !id) return;
    
    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('assessments')
        .update({
          custom_questions: editedQuestions,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      // Update local state
      setAssessment({
        ...assessment,
        custom_questions: editedQuestions,
        updated_at: new Date().toISOString()
      });
      
      setIsEditing(null);
      toast.success('Questions updated successfully');
    } catch (error: any) {
      console.error('Error updating questions:', error);
      toast.error('Failed to update questions: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleInviteDialogClose = () => {
    setShowInviteDialog(false);
    // Refresh candidates list after dialog closes
    fetchCandidates();
  };

  // Update the filteredCandidates logic to include filters and sorting
  const filteredCandidates = candidates
    .filter(candidate => {
      const matchesSearch = candidate.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        candidate.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || candidate.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'name') {
        return sortOrder === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (sortBy === 'score') {
        // First try to use aiRating, fallback to score if aiRating is not available
        const valueA = (a.aiRating !== undefined && a.aiRating !== null) ? a.aiRating : (a.score || 0);
        const valueB = (b.aiRating !== undefined && b.aiRating !== null) ? b.aiRating : (b.score || 0);
        return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
      } else { // date
        // For date sorting, first try completedDate, then fall back to created_at
        const dateA = a.completedDate ? new Date(a.completedDate).getTime() : 
                   (a.created_at ? new Date(a.created_at).getTime() : 0);
        const dateB = b.completedDate ? new Date(b.completedDate).getTime() : 
                   (b.created_at ? new Date(b.created_at).getTime() : 0);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }
    });

  const handleActivateAssessment = async () => {
    if (!user || !assessment || !id) return;
    
    if (userCredits !== null && userCredits < 1) {
      toast.error('You need at least 1 credit to activate this assessment');
      return;
    }
    
    setIsActivating(true);
    
    try {
      // Update the assessment status to active
      const { error: updateError } = await supabase
        .from('assessments')
        .update({ status: 'active' })
        .eq('id', id);
        
      if (updateError) throw updateError;
      
      // No longer decrementing credits here - credits will be decremented when a candidate completes the assessment
      
      // Update local state
      setAssessment(prev => prev ? { ...prev, status: 'active' } : null);
      
      toast.success('Assessment activated successfully!');
    } catch (error: any) {
      console.error('Error activating assessment:', error);
      toast.error(error.message || 'Failed to activate assessment');
    } finally {
      setIsActivating(false);
    }
  };

  const handlePlayPause = () => {
    if (audioRef) {
      if (isPlaying) {
        audioRef.pause();
      } else {
        audioRef.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeToggle = () => {
    if (audioRef) {
      audioRef.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleOpenTranscript = () => {
    setShowTranscript(true);
  };

  const handleShareAssessment = () => {
    if (selectedCandidate) {
      const shareUrl = `${window.location.origin}/assessment/${assessment?.id}/candidate/${selectedCandidate.id}`;
      navigator.clipboard.writeText(shareUrl);
      toast.success('Assessment link copied to clipboard');
    }
  };

  const handleSeek = (value: number[]) => {
    if (audioRef && value.length > 0) {
      audioRef.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleArchiveAssessment = async () => {
    if (!assessment || !id) return;
    
    try {
      setIsArchiving(true);
      
      const { error } = await supabase
        .from('assessments')
        .update({
          status: 'archived',
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      // Update local state
      setAssessment({
        ...assessment,
        status: 'archived' as const,
        updated_at: new Date().toISOString()
      });
      
      toast.success('Assessment archived successfully');
      navigate('/assessments'); // Redirect to assessments list
    } catch (error: any) {
      console.error('Error archiving assessment:', error);
      toast.error('Failed to archive assessment: ' + (error.message || 'Unknown error'));
    } finally {
      setIsArchiving(false);
    }
  };

  const handleDeleteAssessment = async () => {
    if (!assessment || !id) return;
    
    try {
      setIsDeleting(true);
      
      // Delete all related candidates first
      const { error: candidatesError } = await supabase
        .from('candidates')
        .delete()
        .eq('assessment_id', id);
      
      if (candidatesError) throw candidatesError;
      
      // Then delete the assessment
      const { error } = await supabase
        .from('assessments')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      toast.success('Assessment deleted successfully');
      navigate('/assessments'); // Redirect to assessments list
    } catch (error: any) {
      console.error('Error deleting assessment:', error);
      toast.error('Failed to delete assessment: ' + (error.message || 'Unknown error'));
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Add function to fetch completion count
  const fetchCompletionCount = useCallback(async () => {
    if (!id) return;

    try {
      // Get all candidates for this assessment with completed status
      const { data: completedCandidates, error: countError } = await supabase
        .from('candidates')
        .select('id, status')
        .eq('assessment_id', id);

      if (countError) throw countError;

      const completedCount = completedCandidates?.filter(c => c.status === 'completed').length || 0;
      const inProgressCount = completedCandidates?.filter(c => c.status === 'in_progress').length || 0;
      const totalCount = completedCandidates?.length || 0;

      setCompletionRate(`${completedCount} of ${totalCount} completed (${inProgressCount} in progress)`);
    } catch (error) {
      console.error('Error fetching completion count:', error);
    }
  }, [id]);

  // Add useEffect to fetch completion count periodically
  useEffect(() => {
    fetchCompletionCount();

    // Refresh completion count every 30 seconds
    const interval = setInterval(fetchCompletionCount, 30000);

    return () => clearInterval(interval);
  }, [fetchCompletionCount]);

  // Function to check if the organization has an active Knit integration
  const checkActiveIntegration = async () => {
    try {
      // First get the user's organization ID
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user?.id)
        .single();
        
      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        return;
      }
      
      if (!profile?.organization_id) {
        console.log("User has no organization ID");
        return;
      }
      
      // Check if there's an active integration for this organization
      const { data: integrations, error: integrationsError } = await supabase
        .from('knit_integrations')
        .select('id')
        .eq('organization_id', profile.organization_id)
        .eq('active', true)
        .limit(1);
        
      if (integrationsError) {
        console.error("Error fetching integrations:", integrationsError);
        return;
      }
      
      setHasActiveIntegration(integrations !== null && integrations.length > 0);
      console.log("Organization has active integration:", hasActiveIntegration);
    } catch (error) {
      console.error("Error checking for active integrations:", error);
    }
  };

  // Function to handle syncing ATS applicants
  const handleSyncApplicants = async () => {
    try {
      // Record the start time
      const startTime = Date.now();
      
      // Show loading state
      setSyncLoading(true);
      
      // Make the API call
      const result = await startAtsApplicantsSync();
      
      if (result.success) {
        // Calculate how much time has passed since we started
        const elapsedTime = Date.now() - startTime;
        const requiredWaitTime = 5000; // 5 seconds
        
        // If less than 5 seconds have passed, wait for the remaining time
        if (elapsedTime < requiredWaitTime) {
          await new Promise(resolve => setTimeout(resolve, requiredWaitTime - elapsedTime));
        }
        
        // Show success message with guidance
        toast.success(
          <div className="flex flex-col gap-1">
            <p><strong>ATS applicants sync initiated!</strong></p>
            <p className="text-sm">Your applicants are being processed in the background.</p>
            <p className="text-sm">Please wait a few moments and refresh the page to see updates.</p>
          </div>,
          { 
            duration: 8000, // Show toast for 8 seconds
            action: {
              label: "Refresh Now",
              onClick: () => window.location.reload()
            }
          }
        );
        
        // Refresh candidates list after sync
        await fetchCandidates();
      } else {
        toast.error(result.message);
      }
    } catch (error: any) {
      console.error('Error syncing ATS applicants:', error);
      toast.error('Error syncing ATS applicants: ' + (error.message || 'Unknown error'));
    } finally {
      setSyncLoading(false);
    }
  };

  // Handle the Invite Candidates button click with verification check
  const handleInviteClick = () => {
    setShowInviteDialog(true);
  };

  // Add a new function to handle saving skill questions
  const handleSaveSkillQuestions = async () => {
    if (!assessment) return;
    
    try {
      setIsSaving(true);
      
      // Filter out any empty questions
      const filteredSkillQuestions: Record<string, string[]> = {};
      Object.entries(editedSkillQuestions).forEach(([skill, questions]) => {
        filteredSkillQuestions[skill] = questions.filter(q => q.trim() !== '');
      });
      
      // Save to database
      const { error } = await supabase
        .from('assessments')
        .update({ 
          skill_questions: filteredSkillQuestions
        })
        .eq('id', assessment.id);
      
      if (error) throw error;
      
      // Update local state
      setAssessment((prev: AssessmentRow | null) => {
        if (!prev) return null;
        return {
          ...prev,
          skill_questions: filteredSkillQuestions
        };
      });
      
      setIsEditing(null);
      toast.success('Skill questions updated successfully');
    } catch (error: any) {
      console.error("Error saving skill questions:", error);
      toast.error('Failed to update skill questions: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Add a new function to handle saving interview settings
  const handleSaveInterviewSettings = async () => {
    if (!assessment) return;
    
    try {
      setIsSaving(true);
      
      // Find the accent object to get the correct voiceId
      const accentObj = ACCENTS[editedLanguage]?.find(
        (accent) => accent.id === editedAccent
      );
      
      // Use the proper voiceId from our predefined constants
      const voiceId = accentObj?.voiceId || '';
      
      // Save to database
      const { error } = await supabase
        .from('assessments')
        .update({ 
          language: editedLanguage,
          accent: editedAccent,
          voice_id: voiceId
        })
        .eq('id', assessment.id);
      
      if (error) throw error;
      
      // Update local state
      setAssessment((prev: AssessmentRow | null) => {
        if (!prev) return null;
        return {
          ...prev,
          language: editedLanguage,
          accent: editedAccent,
          voice_id: voiceId
        };
      });
      
      setIsEditing(null);
      toast.success('Interview settings updated successfully');
    } catch (error: any) {
      console.error("Error saving interview settings:", error);
      toast.error('Failed to update interview settings: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Function to add a question to the active skill
  const handleAddSkillQuestion = () => {
    if (!activeSkill || !newSkillQuestion.trim()) return;
    
    setEditedSkillQuestions((prev: Record<string, string[]>) => {
      const updatedQuestions = { ...prev };
      // Initialize the array for this skill if it doesn't exist yet
      if (!updatedQuestions[activeSkill]) {
        updatedQuestions[activeSkill] = [];
      }
      updatedQuestions[activeSkill] = [...updatedQuestions[activeSkill], newSkillQuestion.trim()];
      return updatedQuestions;
    });
    
    setNewSkillQuestion('');
  };

  // Function to remove a skill question
  const handleRemoveSkillQuestion = (skill: string, index: number) => {
    setEditedSkillQuestions((prev: Record<string, string[]>) => {
      const updatedQuestions = { ...prev };
      updatedQuestions[skill] = updatedQuestions[skill].filter((_, i) => i !== index);
      return updatedQuestions;
    });
  };

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 overflow-y-auto bg-gradient-to-b from-[#F1F0FB] to-[#E5DEFF] text-[#1A1F2C]">
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center min-h-[300px]">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
              <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span>Error: {error}</span>
              </div>
              <p className="mb-4 text-muted-foreground">
                There was a problem loading the assessment. Please try again.
              </p>
              <Button onClick={handleRetry}>
                Retry
              </Button>
            </div>
          ) : assessment ? (
            <>
              <div className="bg-gradient-to-br from-[#9b87f5] to-[#7C6AD6] rounded-lg p-6 mb-6 relative overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    {isEditing === 'title' ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => navigate('/assessments')}
                          className="text-white/80 hover:text-white hover:bg-white/10 mr-2 h-8 w-8"
                        >
                          <ArrowLeft size={16} />
                        </Button>
                        <Input
                          className="text-2xl font-bold bg-transparent border-white/30 text-white flex-1"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                        />
                        <Badge 
                          className={`ml-2 px-2 py-1 text-white ${
                            assessment?.assessmentType === 'ai-assisted' || assessment?.assessment_type === 'ai-assisted' || assessment?.source !== 'RoundOne'
                              ? "bg-purple-800/30 hover:bg-purple-800/40 border-purple-400/30"
                              : "bg-blue-800/30 hover:bg-blue-800/40 border-blue-400/30"
                          }`}
                        >
                          {assessment?.assessmentType === 'ai-assisted' || assessment?.assessment_type === 'ai-assisted' || assessment?.source !== 'RoundOne' 
                            ? "AI Assisted" 
                            : "Custom Assessment"}
                        </Badge>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="bg-white/20 hover:bg-white/30 text-white border-none"
                          onClick={handleSaveTitle}
                          disabled={isSaving}
                        >
                          {isSaving ? (
                            <Loader2 size={14} className="mr-2 animate-spin" />
                          ) : (
                            <Save size={14} className="mr-2" />
                          )}
                          Save
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => navigate('/assessments')}
                          className="text-white/80 hover:text-white hover:bg-white/10 mr-2 h-8 w-8"
                        >
                          <ArrowLeft size={16} />
                        </Button>
                        <h1 className="text-2xl font-bold text-white">{assessment.title}</h1>
                        <Badge 
                          className={`ml-2 px-2 py-1 text-white ${
                            assessment?.assessmentType === 'ai-assisted' || assessment?.assessment_type === 'ai-assisted' || assessment?.source !== 'RoundOne'
                              ? "bg-purple-800/30 hover:bg-purple-800/40 border-purple-400/30"
                              : "bg-blue-800/30 hover:bg-blue-800/40 border-blue-400/30"
                          }`}
                        >
                          {assessment?.assessmentType === 'ai-assisted' || assessment?.assessment_type === 'ai-assisted' || assessment?.source !== 'RoundOne' 
                            ? "AI Assisted" 
                            : "Custom Assessment"}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setIsEditing('title')}
                          className="hover:bg-white/10 text-white h-8 w-8"
                        >
                          <Edit size={14} />
                        </Button>
                        <div className="ml-auto">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={handleArchiveAssessment}
                                disabled={isArchiving || assessment?.status === 'archived' as const}
                                className="text-muted-foreground"
                              >
                                <Archive className="mr-2 h-4 w-4" />
                                {isArchiving ? 'Archiving...' : 'Archive Assessment'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setShowDeleteDialog(true)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Assessment
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-white/80 space-x-6">
                      <div className="flex items-center">
                        <Calendar size={14} className="mr-2" /> 
                        Created: {new Date(assessment.created_at).toLocaleDateString()}
                      </div>
                      <Badge variant="outline" className="bg-white/10 text-white border-white/20">
                        {assessment.status.charAt(0).toUpperCase() + assessment.status.slice(1)}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
              
              {assessment.status === 'inactive' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 text-yellow-700">
                    <AlertTriangle size={18} />
                    <span className="font-medium">Inactive Assessment</span>
                  </div>
                  <p className="text-sm text-yellow-600 mt-1 mb-3">
                    This assessment is inactive because you don't have enough credits. Activate it to invite candidates.
                  </p>
                  <Button
                    variant="outline"
                    className="bg-yellow-100 border-yellow-300 text-yellow-800 hover:bg-yellow-200"
                    onClick={handleActivateAssessment}
                    disabled={isActivating || (userCredits !== null && userCredits < 1)}
                  >
                    {isActivating ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" /> Activating...
                      </>
                    ) : (
                      <>
                        Activate Assessment (1 Credit)
                      </>
                    )}
                  </Button>
                  {userCredits !== null && userCredits < 1 && (
                    <div className="mt-2 text-xs text-yellow-600">
                      <Link to="/credits" className="underline">Add more credits</Link> to activate this assessment.
                    </div>
                  )}
                </div>
              )}
          
              <div className="grid grid-cols-12 gap-6">
                {/* Candidates Section - Now on the left and larger */}
                <div className="col-span-12 lg:col-span-8">
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>Candidates</CardTitle>
                          <CardDescription className="mt-1">
                            {completionRate}
                          </CardDescription>
                        </div>
                        <div className="flex gap-3">
                          {hasActiveIntegration && assessment?.ats_job_id && (
                            <Button 
                              variant="outline" 
                              onClick={handleSyncApplicants}
                              disabled={syncLoading}
                              className="flex items-center"
                              size="sm"
                            >
                              {syncLoading ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <RefreshCw className="mr-2 h-4 w-4" />
                              )}
                              Sync ATS Applicants
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={handleInviteClick}
                            className="whitespace-nowrap bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] text-white shadow-md hover:shadow-lg transition-all"
                          >
                            <UserPlus size={16} className="mr-2" /> Invite Candidates
                          </Button>
                        </div>
                      </div>
                      <div className="mt-4 space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Search candidates..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="pl-8 w-full"
                            />
                          </div>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="icon" className="h-9 w-9">
                                      <Filter className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-[120px]">
                                    <DropdownMenuItem onClick={() => setStatusFilter('all')}>
                                      All Status
                                      {statusFilter === 'all' && <Check className="ml-auto h-4 w-4" />}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setStatusFilter('invited')}>
                                      Invited
                                      {statusFilter === 'invited' && <Check className="ml-auto h-4 w-4" />}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setStatusFilter('in_progress')}>
                                      In Progress
                                      {statusFilter === 'in_progress' && <Check className="ml-auto h-4 w-4" />}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setStatusFilter('completed')}>
                                      Completed
                                      {statusFilter === 'completed' && <Check className="ml-auto h-4 w-4" />}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Filter by status</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="icon" className="h-9 w-9">
                                      {sortOrder === 'asc' ? (
                                        <SortAsc className="h-4 w-4" />
                                      ) : (
                                        <SortDesc className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-[140px]">
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        if (sortBy === 'date') {
                                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                                        } else {
                                          setSortBy('date');
                                          setSortOrder('desc');
                                        }
                                      }}
                                    >
                                      Sort by Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        if (sortBy === 'name') {
                                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                                        } else {
                                          setSortBy('name');
                                          setSortOrder('asc');
                                        }
                                      }}
                                    >
                                      Sort by Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        if (sortBy === 'score') {
                                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                                        } else {
                                          setSortBy('score');
                                          setSortOrder('desc');
                                        }
                                      }}
                                    >
                                      Sort by Rating {sortBy === 'score' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Sort by: {sortBy}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <div>
                            {filteredCandidates.length} 
                            {statusFilter !== 'all' ? ` ${statusFilter}` : ''} candidate{filteredCandidates.length !== 1 ? 's' : ''}
                          </div>
                          {searchQuery && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSearchQuery('')}
                              className="h-auto p-0 text-muted-foreground hover:text-foreground"
                            >
                              Clear search
                            </Button>
                          )}
                        </div>
                      </div>
                      <Progress 
                        value={(candidates.filter(c => c.status === 'completed' || c.status === 'in_progress').length / candidates.length) * 100} 
                        className="h-2 mt-4"
                        variant="gradient"
                      />
                    </CardHeader>
                    <CardContent className="grid gap-2 max-h-[calc(100vh-300px)] overflow-y-auto pt-2">
                      {loadingCandidates ? (
                        <div className="flex justify-center items-center p-4">
                          <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                      ) : filteredCandidates.length > 0 ? (
                        filteredCandidates.map(candidate => (
                          <CandidateCard
                            key={candidate.id}
                            candidate={candidate}
                            onClick={handleCandidateClick}
                          />
                        ))
                      ) : searchQuery ? (
                        <div className="text-center p-4 text-muted-foreground">
                          No candidates match your search
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                          <UserPlus size={40} className="mb-4 opacity-50" />
                          <p className="mb-2 font-medium">No candidates yet</p>
                          <p className="text-sm mb-4">Start by inviting candidates to take this assessment</p>
                          <Button
                            size="sm"
                            onClick={handleInviteClick}
                            className="whitespace-nowrap bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] text-white shadow-md hover:shadow-lg transition-all"
                          >
                            <UserPlus size={16} className="mr-2" /> Invite Candidates
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Assessment Details Section - Now on the right and smaller */}
                <div className="col-span-12 lg:col-span-4">
                  <div className="space-y-6">
                    {/* Skills Section */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CardTitle>Skills to Assess</CardTitle>
                            {isEditing !== 'skills' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setIsEditing('skills')}
                                className="text-[#9b87f5] hover:bg-[#9b87f5]/10 h-8 w-8"
                              >
                                <Edit size={14} />
                              </Button>
                            )}
                          </div>
                        </div>
                        {isBasicPlan(userSubscription) && isEditing === 'skills' && editedSkills.length >= 3 && (
                          <CardDescription className="text-xs flex items-center gap-1 mt-2 text-orange-600">
                            <AlertCircle size={14} />
                            Basic plan is limited to 3 skills. 
                            <Link to="/credits" className="text-[#7662DA] hover:underline flex items-center gap-0.5">
                              Upgrade <ExternalLink size={12} />
                            </Link>
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {editedSkills.map((skill, index) => (
                            <Badge 
                              key={index} 
                              className="py-1.5 px-3.5 text-sm bg-[#9b87f5]/20 text-[#9b87f5] rounded-full border-none hover:bg-[#9b87f5]/30 font-medium flex items-center gap-1.5"
                            >
                              {skill}
                              {isEditing === 'skills' && (
                                <button 
                                  onClick={() => handleRemoveSkill(skill)} 
                                  className="ml-1 hover:text-red-300 focus:outline-none"
                                  aria-label={`Remove ${skill}`}
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </Badge>
                          ))}
                          
                          {isEditing === 'skills' && (
                            <>
                              {isAddingSkill ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    ref={newSkillInputRef}
                                    placeholder="Type skill & press Enter"
                                    value={newSkill}
                                    onChange={(e) => setNewSkill(e.target.value)}
                                    className="h-8 w-48 py-1 px-2 text-sm"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleAddSkill();
                                      } else if (e.key === 'Escape') {
                                        setIsAddingSkill(false);
                                        setNewSkill('');
                                      }
                                    }}
                                    onBlur={() => {
                                      if (newSkill.trim()) {
                                        handleAddSkill();
                                      } else {
                                        setIsAddingSkill(false);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-[#9b87f5] border-[#9b87f5]"
                                    onClick={handleSaveSkills}
                                    disabled={isSaving}
                                  >
                                    {isSaving ? (
                                      <Loader2 size={14} className="mr-2 animate-spin" />
                                    ) : (
                                      <Save size={14} className="mr-2" />
                                    )}
                                    Save
                                  </Button>
                                </div>
                              ) : (
                                <Badge 
                                  className={`py-1.5 px-3.5 text-sm cursor-pointer flex items-center bg-[#9b87f5]/10 text-[#9b87f5] rounded-full border-none hover:bg-[#9b87f5]/20 font-medium ${isBasicPlan(userSubscription) && editedSkills.length >= 3 ? 'opacity-50 cursor-not-allowed': ''}`}
                                  onClick={() => {
                                    if (isBasicPlan(userSubscription) && editedSkills.length >= 3) {
                                      setPremiumDialogContext('skills');
                                      setShowPremiumDialog(true);
                                    } else {
                                      setIsAddingSkill(true);
                                    }
                                  }}
                                >
                                  <Plus size={14} className="mr-1" /> Add skill
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Questions Section - Replaced with Tabbed View */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CardTitle>Assessment Questions</CardTitle>
                            {isEditing !== 'questions' && isEditing !== 'skillQuestions' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  if (isBasicPlan(userSubscription)) {
                                    setPremiumDialogContext('questions');
                                    setShowPremiumDialog(true);
                                  } else {
                                    setIsEditing('questions');
                                  }
                                }}
                                className="text-[#9b87f5] hover:bg-[#9b87f5]/10 h-8 w-8"
                              >
                                <Edit size={14} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {isEditing === 'questions' || isEditing === 'skillQuestions' ? (
                          // Edit Mode
                          <div className="space-y-6">
                            {/* Tabs for editing questions */}
                            <Tabs defaultValue="general" className="w-full">
                              <TabsList className="w-full flex overflow-x-auto mb-4">
                                <TabsTrigger 
                                  value="general"
                                  className={`flex-1 ${
                                    activeSkill === null ? 'bg-[#9b87f5] text-white' : ''
                                  }`}
                                  onClick={() => setActiveSkill(null)}
                                >
                                  General
                                </TabsTrigger>
                                {editedSkills.map((skill) => (
                                  <TabsTrigger 
                                    key={skill} 
                                    value={skill}
                                    className={`flex-1 ${
                                      activeSkill === skill ? 'bg-[#9b87f5] text-white' : ''
                                    }`}
                                    onClick={() => setActiveSkill(skill)}
                                  >
                                    {skill}
                                  </TabsTrigger>
                                ))}
                              </TabsList>
                              
                              {/* Tab content - automatically handled by selection of activeSkill */}
                              <div className="mt-4">
                                {activeSkill === null ? (
                                  // General Questions editing
                                  <div className="space-y-4">
                                    <h3 className="text-lg font-medium">General Questions</h3>
                                    {editedQuestions.map((question, index) => (
                                      <div key={index} className="group relative bg-white/50 dark:bg-card p-4 rounded-md border border-border">
                                        <p className="pr-8">{question}</p>
                                        <button
                                          onClick={() => handleRemoveQuestion(question)}
                                          className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
                                          aria-label={`Remove question ${index + 1}`}
                                        >
                                          <X size={18} />
                                        </button>
                                      </div>
                                    ))}
                                    {editedQuestions.length === 0 && (
                                      <div className="text-muted-foreground text-sm p-4 text-center">
                                        No general questions added yet
                                      </div>
                                    )}
                                    <div className="flex items-start gap-2 mt-4">
                                      <Textarea
                                        placeholder="Add a new general question..."
                                        value={newQuestion}
                                        onChange={(e) => setNewQuestion(e.target.value)}
                                        className="min-h-[80px] flex-1"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && e.ctrlKey) {
                                            handleAddQuestion();
                                          }
                                        }}
                                      />
                                      <Button 
                                        onClick={handleAddQuestion}
                                        variant="secondary"
                                        className="shrink-0"
                                      >
                                        <Plus size={16} className="mr-1" /> Add
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  // Skill Questions editing
                                  <div className="space-y-4">
                                    <h3 className="text-lg font-medium">Questions for {activeSkill}</h3>
                                    {editedSkillQuestions[activeSkill]?.map((question, index) => (
                                      <div key={index} className="group relative bg-white/50 dark:bg-card p-4 rounded-md border border-border">
                                        <p className="pr-8">{question}</p>
                                        <button
                                          onClick={() => handleRemoveSkillQuestion(activeSkill, index)}
                                          className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
                                          aria-label={`Remove question ${index + 1}`}
                                        >
                                          <X size={18} />
                                        </button>
                                      </div>
                                    ))}
                                    {(!editedSkillQuestions[activeSkill] || editedSkillQuestions[activeSkill].length === 0) && (
                                      <div className="text-muted-foreground text-sm p-4 text-center">
                                        No questions for {activeSkill} yet
                                      </div>
                                    )}
                                    <div className="flex items-start gap-2 mt-4">
                                      <Textarea
                                        placeholder={`Add a question for ${activeSkill}...`}
                                        value={newSkillQuestion}
                                        onChange={(e) => setNewSkillQuestion(e.target.value)}
                                        className="min-h-[80px] flex-1"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && e.ctrlKey) {
                                            handleAddSkillQuestion();
                                          }
                                        }}
                                      />
                                      <Button 
                                        onClick={handleAddSkillQuestion}
                                        variant="secondary"
                                        className="shrink-0"
                                      >
                                        <Plus size={16} className="mr-1" /> Add
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </Tabs>
                            
                            {/* Save/Cancel Buttons */}
                            <div className="flex justify-end mt-6 pt-4 border-t border-border">
                              <Button
                                variant="outline"
                                className="mr-2"
                                onClick={() => setIsEditing(null)}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="default"
                                className="bg-[#9b87f5] hover:bg-[#8a78e0]"
                                onClick={activeSkill === null ? handleSaveQuestions : handleSaveSkillQuestions}
                                disabled={isSaving}
                              >
                                {isSaving ? (
                                  <Loader2 size={14} className="mr-2 animate-spin" />
                                ) : (
                                  <Save size={14} className="mr-2" />
                                )}
                                Save Questions
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <Tabs defaultValue="general" className="w-full">
                            <TabsList className="w-full flex overflow-x-auto mb-4">
                              <TabsTrigger value="general">
                                General 
                                {assessment?.custom_questions?.length > 0 && (
                                  <Badge className="ml-2 bg-[#9b87f5]/20 text-[#9b87f5]">
                                    {assessment.custom_questions.length}
                                  </Badge>
                                )}
                              </TabsTrigger>
                              {assessment?.skill_questions && Object.keys(assessment.skill_questions).map((skill) => {
                                const questions = assessment.skill_questions?.[skill] || [];
                                return (
                                  <TabsTrigger key={skill} value={skill}>
                                    {skill}
                                    {questions.length > 0 && (
                                      <Badge className="ml-2 bg-[#9b87f5]/20 text-[#9b87f5]">
                                        {questions.length}
                                      </Badge>
                                    )}
                                  </TabsTrigger>
                                );
                              })}
                            </TabsList>
                            
                            <TabsContent value="general">
                              <div className="space-y-4">
                                <h3 className="text-lg font-medium">General Questions</h3>
                                {assessment?.custom_questions?.length > 0 ? (
                                  assessment.custom_questions.map((question, index) => (
                                    <div key={index} className="bg-white/50 dark:bg-card p-4 rounded-md border border-border">
                                      {question}
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-center text-muted-foreground py-4">
                                    No general questions added
                                  </div>
                                )}
                              </div>
                            </TabsContent>
                            
                            {assessment?.skill_questions && Object.keys(assessment.skill_questions).map((skill) => {
                              const questions = assessment.skill_questions?.[skill] || [];
                              return (
                                <TabsContent key={skill} value={skill}>
                                  <div className="space-y-4">
                                    <h3 className="text-lg font-medium">Questions for {skill}</h3>
                                    {questions.length > 0 ? (
                                      questions.map((question, index) => (
                                        <div key={index} className="bg-white/50 dark:bg-card p-4 rounded-md border border-border">
                                          {question}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="text-center text-muted-foreground py-4">
                                        No questions for {skill}
                                      </div>
                                    )}
                                  </div>
                                </TabsContent>
                              );
                            })}
                          </Tabs>
                        )}
                      </CardContent>
                    </Card>

                    {/* Interview Settings Section */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CardTitle>Interview Settings</CardTitle>
                            {isEditing !== 'interviewSettings' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setIsEditing('interviewSettings')}
                                className="text-[#9b87f5] hover:bg-[#9b87f5]/10 h-8 w-8"
                              >
                                <Edit size={14} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {isEditing === 'interviewSettings' ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm font-medium mb-1 block">Language</label>
                                <Select value={editedLanguage} onValueChange={setEditedLanguage}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select language" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {LANGUAGES.map((language) => (
                                      <SelectItem key={language.id} value={language.id}>
                                        {language.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div>
                                <label className="text-sm font-medium mb-1 block">Accent</label>
                                <Select value={editedAccent} onValueChange={setEditedAccent}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select accent" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ACCENTS[editedLanguage]?.map((accent) => (
                                      <SelectItem key={accent.id} value={accent.id}>
                                        {accent.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            <p className="text-xs text-muted-foreground mt-1">
                              Select the language and accent for the AI interviewer
                            </p>
                            
                            <div className="flex justify-end mt-4">
                              <Button
                                variant="outline"
                                className="mr-2"
                                onClick={() => setIsEditing(null)}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="default"
                                className="bg-[#9b87f5] hover:bg-[#8a78e0]"
                                onClick={handleSaveInterviewSettings}
                                disabled={isSaving}
                              >
                                {isSaving ? (
                                  <Loader2 size={14} className="mr-2 animate-spin" />
                                ) : (
                                  <Save size={14} className="mr-2" />
                                )}
                                Save Settings
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            {assessment?.language || assessment?.accent ? (
                              <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                  <Mic className="h-5 w-5 text-[#9b87f5]" />
                                  <div>
                                    <span className="font-medium">Voice Settings:</span>{' '}
                                    <Badge variant="outline" className="bg-purple-50 text-purple-700">
                                      {assessment?.language ? assessment.language.toUpperCase() : 'EN'}-
                                      {assessment?.accent ? assessment.accent.toUpperCase() : 'US'}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center text-muted-foreground py-4">
                                No voice settings configured. Edit to add voice settings.
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
              
              <InviteCandidatesDialog
                isOpen={showInviteDialog}
                onClose={handleInviteDialogClose}
                assessmentId={assessment.id}
                assessmentTitle={assessment.title}
              />
            </>
          ) : (
            <div className="flex justify-center items-center min-h-[300px]">
              <p>Assessment not found</p>
            </div>
          )}

          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Assessment</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this assessment? This action cannot be undone.
                  All candidate data associated with this assessment will also be deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAssessment}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Assessment'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Add Premium Feature Dialog */}
          <Dialog open={showPremiumDialog} onOpenChange={setShowPremiumDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-yellow-500" />
                  Premium Feature
                </DialogTitle>
                <DialogDescription>
                  {premiumDialogContext === 'skills' ? 
                    "Adding more than 3 skills is available on Premium and Agency plans." :
                    "Custom assessment questions are available on Premium and Agency plans."}
                  {" "}Upgrade to unlock additional features.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 bg-orange-50 border border-orange-200 rounded-md p-3 text-orange-800 text-sm">
                <div className="flex items-start gap-2">
                  <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">This feature requires a Premium or Agency subscription.</p>
                    <p className="mt-1 text-xs text-orange-700">
                      {premiumDialogContext === 'skills' ?
                        "Upgrade your plan to add more skills to your assessments." :
                        "Upgrade your plan to customize assessment questions."}
                    </p>
                  </div>
                </div>
              </div>
              <DialogFooter className="flex justify-end gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPremiumDialog(false);
                    if (premiumDialogContext === 'skills') {
                      setNewSkill('');
                    }
                  }}
                >
                  Maybe Later
                </Button>
                <Button
                  className="bg-gradient-to-r from-[#FFB168] to-[#FF8C37] text-white hover:from-[#FFA158] hover:to-[#FF7C27]"
                  onClick={() => {
                    setShowPremiumDialog(false);
                    window.location.href = '/credits';
                  }}
                >
                  <ExternalLink size={16} className="mr-2" />
                  Upgrade Now
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        
        {/* Candidate Details Sheet */}
        {selectedCandidate && (
          <CandidateSheet
            open={true}
            onOpenChange={(open) => !open && handleCloseCandidate()}
            candidate={selectedCandidate}
            candidateAssessment={candidateAssessment}
            onRefresh={fetchCandidates}
          />
        )}

        {/* Replace the Transcript Sheet with AssessmentTranscript component */}
        <AssessmentTranscript
          isOpen={showTranscript}
          onClose={() => setShowTranscript(false)}
          transcript={candidateAssessment?.transcript}
        />
      </main>
    </div>
  );
};

export default AssessmentDetail;
